import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import * as d3 from 'd3';

interface CatalogueNode {
  name: string;
  description?: string;
  value?: number | string;
  impact?: 'positive' | 'negative' | 'neutral' | 'critical';
  children?: CatalogueNode[];
  expanded?: boolean;
  metrics?: {
    percentage?: number;
    trend?: 'up' | 'down' | 'stable';
    priority?: 'high' | 'medium' | 'low';
  };
}

@Component({
  selector: 'mzic-catalogue-analysis-tree',
  standalone: true,
  imports: [CommonModule, TranslocoModule],
  templateUrl: './catalogue-analysis-tree.component.html',
  styleUrls: ['./catalogue-analysis-tree.component.scss'],
})
export class CatalogueAnalysisTreeComponent implements OnInit, AfterViewInit {
  @ViewChild('treeContainer', { static: true }) treeContainer!: ElementRef;

  private svg: any;
  private treeLayout: any;
  private root: any;
  private width = 1600;
  private height = 2400;
  private margin = { top: 40, right: 200, bottom: 40, left: 200 };
  private duration = 750;
  private nodeId = 0;

  isFullscreen = false;

  private translocoService = inject(TranslocoService);

  catalogueData: CatalogueNode = {
    name: 'Catalogue Analysis',
    impact: 'neutral',
    children: [
      {
        name: 'Catalogue Type',
        impact: 'neutral',
        children: [
          {
            name: 'Works Catalogue',
            impact: 'neutral',
            children: [
              {
                name: 'Composer Analysis',
                impact: 'neutral',
                children: [
                  {
                    name: 'Long-term Potential',
                    description:
                      'Evaluate if composer has sustainable career trajectory',
                    impact: 'positive',
                    metrics: { priority: 'high' },
                  },
                ],
              },
              {
                name: 'Asset Count',
                impact: 'neutral',
                children: [
                  {
                    name: 'Number of Works',
                    value: 'Total compositional output',
                    impact: 'neutral',
                  },
                ],
              },
            ],
          },
          {
            name: 'Sound Recordings',
            impact: 'neutral',
            children: [
              {
                name: 'Number of Recordings',
                value: 'Total recorded assets',
                impact: 'neutral',
              },
            ],
          },
        ],
      },
      {
        name: 'Revenue Analysis',
        impact: 'critical',
        children: [
          {
            name: 'With Revenue',
            impact: 'positive',
            children: [
              {
                name: 'Revenue Distribution',
                impact: 'critical',
                children: [
                  {
                    name: 'Public Performance',
                    description: 'ECAD, radio, shows revenue',
                    impact: 'positive',
                    metrics: { percentage: 45, trend: 'stable' },
                    children: [
                      {
                        name: 'Active Revenue Analysis',
                        description: 'Radio, shows require active investment',
                        impact: 'negative',
                        children: [
                          {
                            name: 'Long-term Sustainability',
                            description:
                              'Will diminish rapidly without active engagement',
                            impact: 'negative',
                            value: 'Impacts multiplier',
                          },
                        ],
                      },
                    ],
                  },
                  {
                    name: 'Streaming Revenue',
                    description: 'Digital platforms income',
                    impact: 'positive',
                    metrics: { percentage: 55, trend: 'up' },
                  },
                ],
              },
              {
                name: 'Revenue Concentration',
                impact: 'critical',
                children: [
                  {
                    name: 'Pareto Analysis',
                    description: 'Revenue distribution across assets',
                    impact: 'neutral',
                    children: [
                      {
                        name: 'High Concentration Risk',
                        description:
                          'Too much concentration lowers DCF pricing multiplier',
                        impact: 'negative',
                        metrics: { priority: 'high' },
                      },
                    ],
                  },
                ],
              },
              {
                name: 'Sync Revenue',
                impact: 'critical',
                children: [
                  {
                    name: 'Percentage of Total',
                    description: 'High sync % strongly impacts multiplier',
                    impact: 'critical',
                    metrics: { priority: 'high' },
                    children: [
                      {
                        name: 'Forecast Difficulty',
                        description:
                          'Sync revenue hard to predict - needs manual analysis',
                        impact: 'negative',
                      },
                      {
                        name: 'TV Show Placements',
                        description: 'Can bring significant revenue to work',
                        impact: 'positive',
                        value: 'Requires manual analysis',
                      },
                    ],
                  },
                ],
              },
              {
                name: 'Revenue Patterns',
                impact: 'neutral',
                children: [
                  {
                    name: 'Seasonality',
                    description: 'Periodic revenue patterns',
                    impact: 'neutral',
                    value: 'Requires data cleaning',
                  },
                  {
                    name: 'Growth Decay',
                    description: 'Revenue decline rate over time',
                    impact: 'negative',
                    metrics: { trend: 'down' },
                  },
                ],
              },
            ],
          },
          {
            name: 'Without Revenue',
            impact: 'neutral',
            children: [
              {
                name: 'Release Status',
                impact: 'neutral',
                children: [
                  {
                    name: 'Not Released',
                    impact: 'neutral',
                    children: [
                      {
                        name: 'Recording Status',
                        impact: 'neutral',
                        children: [
                          {
                            name: 'Recorded with Major Artist',
                            description: 'High potential for future revenue',
                            impact: 'positive',
                            metrics: { priority: 'high' },
                          },
                          {
                            name: 'Not Recorded',
                            description: 'Lowest potential',
                            impact: 'negative',
                          },
                        ],
                      },
                    ],
                  },
                  {
                    name: 'Released but No Revenue',
                    description: 'May indicate market challenges',
                    impact: 'negative',
                  },
                ],
              },
              {
                name: 'Potential Analysis',
                impact: 'neutral',
                children: [
                  {
                    name: 'Lyrics Correlation',
                    description:
                      'Compare with successful lyrics in genre via RAG database',
                    impact: 'positive',
                    children: [
                      {
                        name: 'Strong Correlation',
                        description: 'Indicates great potential',
                        impact: 'positive',
                        metrics: { priority: 'high' },
                      },
                    ],
                  },
                  {
                    name: 'Audio Characteristics',
                    description: 'Sound features analysis via RAG database',
                    impact: 'positive',
                    children: [
                      {
                        name: 'Genre Match',
                        description: 'Strong correlation indicates potential',
                        impact: 'positive',
                        metrics: { priority: 'high' },
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        name: 'Per-Asset Analysis',
        impact: 'critical',
        children: [
          {
            name: 'Retained Receivables',
            description: 'ECAD retained income search',
            impact: 'positive',
            value: 'Active in-depth search required',
            metrics: { priority: 'high' },
          },
          {
            name: 'Asset Age',
            impact: 'neutral',
            children: [
              {
                name: 'Older Assets with Stable Revenue',
                description: 'Sign of stable income stream',
                impact: 'positive',
                value: 'Impacts multiplier positively',
                metrics: { trend: 'stable' },
              },
              {
                name: 'New Assets',
                description: 'Higher growth potential but uncertain',
                impact: 'neutral',
              },
            ],
          },
          {
            name: 'Recording Artist Analysis',
            impact: 'critical',
            children: [
              {
                name: 'Release Frequency',
                impact: 'neutral',
                children: [
                  {
                    name: 'Too Many Releases',
                    description: 'Offer dilution impacts revenue',
                    impact: 'negative',
                    value: 'Impacts multiplier negatively',
                  },
                ],
              },
              {
                name: 'Social Media Presence',
                impact: 'positive',
                children: [
                  {
                    name: 'Growing Presence',
                    description: 'Can positively impact revenue',
                    impact: 'positive',
                    value: 'Impacts multiplier',
                    metrics: { trend: 'up', priority: 'medium' },
                  },
                ],
              },
              {
                name: 'Audience Profile',
                impact: 'neutral',
                children: [
                  {
                    name: 'Consumption Resilience',
                    description: 'Stable audience consumption patterns',
                    impact: 'positive',
                    value: 'Impacts multiplier',
                  },
                ],
              },
              {
                name: 'Spotify Features',
                impact: 'neutral',
                children: [
                  {
                    name: 'Correlation Analysis',
                    description:
                      'High correlation shows growth/stability signs',
                    impact: 'positive',
                    value: 'Impacts multiplier',
                    metrics: { priority: 'medium' },
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  };

  ngOnInit(): void {
    this.initializeTree();
  }

  ngAfterViewInit(): void {
    this.createVisualization();
    // Center the view initially
    setTimeout(() => {
      this.scrollToTop();
    }, 100);
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.updateDimensions();
    this.updateTree(this.root);
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.isFullscreen) {
      this.toggleFullscreen();
    }
  }

  private initializeTree(): void {
    this.treeLayout = d3
      .tree()
      .size([
        this.height - this.margin.top - this.margin.bottom,
        this.width - this.margin.left - this.margin.right,
      ]);

    this.root = d3.hierarchy(this.catalogueData);
    this.root.x0 = this.height / 2;
    this.root.y0 = 0;

    // Collapse all nodes initially except first two levels
    this.root.descendants().forEach((d: any, i: number) => {
      d.id = i;
      if (d.depth >= 2) {
        d._children = d.children;
        d.children = null;
      }
    });
  }

  private createVisualization(): void {
    const container = d3.select(this.treeContainer.nativeElement);

    // Create SVG with fixed dimensions for scrolling
    this.svg = container
      .append('svg')
      .attr('width', this.width)
      .attr('height', this.height)
      .style('display', 'block')
      .append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`);

    this.updateTree(this.root);
  }

  private updateTree(source: any): void {
    const treeData = this.treeLayout(this.root);
    const nodes = treeData.descendants();
    const links = treeData.links();

    // Normalize for fixed-depth with more spacing for better readability
    nodes.forEach((d: any) => {
      d.y = d.depth * 320; // Increased spacing between levels
    });

    // Update nodes
    const node = this.svg
      .selectAll('g.node')
      .data(nodes, (d: any) => d.id || (d.id = ++this.nodeId));

    // Enter new nodes
    const nodeEnter = node
      .enter()
      .append('g')
      .attr('class', 'node')
      .attr('transform', (d: any) => `translate(${source.y0},${source.x0})`)
      .on('click', (event: any, d: any) => this.click(d));

    // Add rectangles for nodes
    nodeEnter
      .append('rect')
      .attr(
        'class',
        (d: any) => `node-rect impact-${d.data.impact || 'neutral'}`,
      )
      .attr('width', 1e-6)
      .attr('height', 1e-6)
      .attr('rx', 6)
      .attr('ry', 6);

    // Add labels for nodes
    nodeEnter
      .append('text')
      .attr('dy', '.35em')
      .attr('x', 10)
      .attr('text-anchor', 'start')
      .text((d: any) => d.data.name)
      .style('fill-opacity', 1e-6);

    // Add description if exists
    nodeEnter
      .append('text')
      .attr('class', 'node-description')
      .attr('dy', '1.5em')
      .attr('x', 10)
      .text((d: any) => d.data.description || '')
      .style('fill-opacity', 1e-6);

    // Add value if exists
    nodeEnter
      .append('text')
      .attr('class', 'node-value')
      .attr('dy', '2.5em')
      .attr('x', 10)
      .text((d: any) => d.data.value || '')
      .style('fill-opacity', 1e-6);

    // Add metrics badges
    nodeEnter.each(function (this: any, d: any) {
      if (d.data.metrics) {
        const g = d3.select(this);
        const yOffset = -10;

        if (d.data.metrics.percentage !== undefined) {
          g.append('rect')
            .attr('class', 'metric-badge')
            .attr('x', 200)
            .attr('y', yOffset)
            .attr('width', 50)
            .attr('height', 20)
            .attr('rx', 3);

          g.append('text')
            .attr('class', 'metric-text')
            .attr('x', 225)
            .attr('y', yOffset + 14)
            .attr('text-anchor', 'middle')
            .text(`${d.data.metrics.percentage}%`);
        }

        if (d.data.metrics.trend) {
          const trendSymbol =
            d.data.metrics.trend === 'up'
              ? '↑'
              : d.data.metrics.trend === 'down'
                ? '↓'
                : '→';
          g.append('text')
            .attr('class', `trend-${d.data.metrics.trend}`)
            .attr('x', 260)
            .attr('y', 5)
            .text(trendSymbol);
        }

        if (d.data.metrics.priority) {
          g.append('circle')
            .attr('class', `priority-${d.data.metrics.priority}`)
            .attr('cx', 280)
            .attr('cy', 0)
            .attr('r', 4);
        }
      }
    });

    // Update
    const nodeUpdate = nodeEnter.merge(node as any);

    nodeUpdate
      .transition()
      .duration(this.duration)
      .attr('transform', (d: any) => `translate(${d.y},${d.x})`);

    nodeUpdate
      .select('rect.node-rect')
      .attr('width', 240)
      .attr('height', (d: any) => {
        const hasDescription = d.data.description ? 20 : 0;
        const hasValue = d.data.value ? 20 : 0;
        return 40 + hasDescription + hasValue;
      })
      .attr('y', -20);

    nodeUpdate.select('text').style('fill-opacity', 1);

    nodeUpdate.select('text.node-description').style('fill-opacity', 0.7);

    nodeUpdate.select('text.node-value').style('fill-opacity', 0.6);

    // Remove exiting nodes
    const nodeExit = node
      .exit()
      .transition()
      .duration(this.duration)
      .attr('transform', (d: any) => `translate(${source.y},${source.x})`)
      .remove();

    nodeExit.select('rect').attr('width', 1e-6).attr('height', 1e-6);

    nodeExit.select('text').style('fill-opacity', 1e-6);

    // Update links
    const link = this.svg
      .selectAll('path.link')
      .data(links, (d: any) => d.target.id);

    // Enter new links
    const linkEnter = link
      .enter()
      .insert('path', 'g')
      .attr('class', 'link')
      .attr('d', (d: any) => {
        const o = { x: source.x0, y: source.y0 };
        return this.diagonal(o, o);
      });

    // Update
    const linkUpdate = linkEnter.merge(link as any);

    linkUpdate
      .transition()
      .duration(this.duration)
      .attr('d', (d: any) => this.diagonal(d.source, d.target));

    // Remove exiting links
    link
      .exit()
      .transition()
      .duration(this.duration)
      .attr('d', (d: any) => {
        const o = { x: source.x, y: source.y };
        return this.diagonal(o, o);
      })
      .remove();

    // Store old positions for transition
    nodes.forEach((d: any) => {
      d.x0 = d.x;
      d.y0 = d.y;
    });
  }

  private diagonal(s: any, d: any): string {
    return `M ${s.y} ${s.x}
            C ${(s.y + d.y) / 2} ${s.x},
              ${(s.y + d.y) / 2} ${d.x},
              ${d.y} ${d.x}`;
  }

  private click(d: any): void {
    if (d.children) {
      d._children = d.children;
      d.children = null;
    } else {
      d.children = d._children;
      d._children = null;
    }
    this.updateTree(d);
  }

  private updateDimensions(): void {
    // Keep fixed dimensions for scrolling
    // The container will handle scrolling if content exceeds viewport
    if (this.svg) {
      d3.select(this.treeContainer.nativeElement)
        .select('svg')
        .attr('width', this.width)
        .attr('height', this.height);
    }
  }

  expandAll(): void {
    this.expand(this.root);
    this.updateTree(this.root);
    this.scrollToTop();
  }

  collapseAll(): void {
    this.collapse(this.root);
    this.updateTree(this.root);
    this.scrollToTop();
  }

  private scrollToTop(): void {
    // Scroll the viewport to the top-left corner
    if (this.treeContainer && this.treeContainer.nativeElement) {
      this.treeContainer.nativeElement.scrollTop = 0;
      this.treeContainer.nativeElement.scrollLeft = 0;
    }
  }

  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;

    // Adjust dimensions when entering/exiting fullscreen
    if (this.isFullscreen) {
      // Increase dimensions for fullscreen mode
      this.width = 2400;
      this.height = 3000;
    } else {
      // Reset to normal dimensions
      this.width = 1600;
      this.height = 2400;
    }

    // Update the SVG dimensions
    if (this.svg) {
      d3.select(this.treeContainer.nativeElement)
        .select('svg')
        .attr('width', this.width)
        .attr('height', this.height);

      // Re-initialize the tree layout with new dimensions
      this.treeLayout = d3
        .tree()
        .size([
          this.height - this.margin.top - this.margin.bottom,
          this.width - this.margin.left - this.margin.right,
        ]);

      // Update the tree with new dimensions
      this.updateTree(this.root);
    }
  }

  private expand(d: any): void {
    if (d._children) {
      d.children = d._children;
      d._children = null;
    }
    if (d.children) {
      d.children.forEach((child: any) => this.expand(child));
    }
  }

  private collapse(d: any): void {
    if (d.children) {
      d._children = d.children;
      d.children = null;
      d._children.forEach((child: any) => this.collapse(child));
    }
  }
}
